import { useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildAppHref } from 'src/lib/routing';
import {
  createSearchClient,
  type MultiSearchLeg,
  retryOnRefusedKey,
  type SearchClient,
  type SearchDocument,
  type SearchHighlight,
  useSearchCredentials,
} from '../../entities/search';
import { type ResolvedSlot, selectSlots } from '../components/palette/slotSelection';
import {
  type AccentKey,
  type CollectionUiConfig,
  getCollectionConfig,
  readString,
  typesenseRanking,
} from '../constants/collections';
import type { SearchSection } from '../constants/sections';
import { fromAlgoliaHit } from '../utils/algoliaHits';
import {
  buildLegFilter,
  entitledRenderableSections,
  resolveQuerySections,
  sectionBaseFilter,
} from '../utils/composeLegs';
import { type Filter, filterId } from '../utils/filters';

/**
 * Keyword-only search (no vector) across the caller's entitled SECTIONS. A section
 * is a collection optionally narrowed by an already-faceted discriminator, so the
 * four `orders` sub-types and the two `bills` sub-types each get their OWN leg and
 * guaranteed representation — relevance can't hand one federated `orders` leg 20
 * sales orders and zero POs. The scoped key already pins org tenancy + collection
 * visibility, so this hook never chooses what it can read — only what the key was
 * minted for.
 *
 *   axis        input       effect
 *   ────────────────────────────────────────────────────────────────
 *   scope       depth       which ONE section renders (SCOPED view, 20/page)
 *   type filter breadth     which sections' legs are issued (OR across legs)
 *   lifecycle   breadth     is:open/is:closed status-set clause AND'd per leg
 *   status      scope-bound scoped sub-facet — a single status value per leg
 *
 * Every leg carries its section's discriminator in its filter. A `status` filter
 * and `is:open`/`is:closed` (a `lifecycle` filter) narrow further via additional
 * clauses that COMPOSE with the discriminator (`orderType = … AND status = …`) —
 * a `status` filter is gated on the returned status buckets so only a
 * legal value reaches the filter grammar (a second facet-only leg keeps the full
 * bucket list switchable), while `is:open`/`is:closed` expands to the section's
 * trusted status enum set. TYPE is realised as LEG SELECTION (each section keeps
 * its own leg + guaranteed slot), NOT a merged filter. Ranking rides in the
 * projection, so we send NO sort and let each collection's own default sort break
 * ties after relevance. Each hit's score rides through for a cross-collection
 * "Best match".
 */

export const SEARCH_DEBOUNCE_MS = 200;
const RESULTS_PER_COLLECTION = 5;
// Tokens of context Typesense keeps on each side of a match inside a long text
// blob — enough to read the matched slot without shipping the whole field.
const HIGHLIGHT_AFFIX_NUM_TOKENS = 4;
// The scoped tab is a focused, single-type list, so it pages deeper than the
// federated groups (which cap tight to keep every type visible above the fold).
const SCOPED_RESULTS_PER_PAGE = 20;

export interface SearchResultItem {
  /** Stable document id (`SalesOrder-42`, an account path, …); an Algolia hit's `objectID`. */
  id: string;
  /** The underlying collection (Best-match de-dup identity, recents key). */
  collectionKey: string;
  /** The section this hit was found in (group key, per-type facet identity). */
  sectionKey: string;
  config: CollectionUiConfig;
  accentKey: AccentKey;
  /** Line-1 monospace identifier (SO-00529, a lot number, an account number…). */
  identifier?: string;
  /** Line-1 uppercase entity-type label (SALES ORDER, LOT, INVOICE…). */
  typeLabel: string;
  status?: string;
  /** Line-2 labelled slots, already selected + capped at 3 (matched slot first). */
  slots: ResolvedSlot[];
  route: string;
  /** Relevance score; drives cross-collection "Best match" selection. */
  textMatch: number;
  document: SearchDocument;
  /** `fieldName → { snippet }` — which document field matched drives the leading
   *  slot and its `<mark>`-highlighted snippet. Empty for recents. */
  highlight: SearchHighlight;
}

export interface StatusFacet {
  value: string;
  count: number;
}

export interface SearchResultGroup {
  key: string;
  section: SearchSection;
  /** Total matches in the section (for the facet count), ≥ `items.length`. */
  found: number;
  items: SearchResultItem[];
}

export interface GlobalSearchState {
  groups: SearchResultGroup[];
  totalFound: number;
  /** Status buckets for the scoped section (empty in the all view). */
  statusFacets: StatusFacet[];
  loading: boolean;
  /** Latched on request COMPLETION, never on data count: `false` only while the first request for the current non-empty query stream is in flight, `true` once any response settles (results, zero, or error), re-arming to `false` when the query empties. The palette drives its skeleton off THIS. */
  settled: boolean;
  error: Error | null;
}

export interface UseGlobalSearchArgs {
  /** The app-root search client; when null the hook stays idle. */
  client: SearchClient | null;
  /** Entitled collection aliases from the token. */
  collections: string[];
  query: string;
  /** Depth axis: the ONE section key the scoped view renders, or null (all view). */
  scope: string | null;
  /** Breadth + scope-bound narrowings: type (leg selection), lifecycle, status. */
  filters: readonly Filter[];
  /**
   * Top-level type facet (the filter-row selection). When set it is AUTHORITATIVE —
   * it selects the breadth and overrides any `type` token in the text. Null = unset.
   */
  typeFacet?: string | null;
  /**
   * Drive requests only while the palette is open (default true). The header bar
   * is typeable while closed, so without this a keystroke there would fire a
   * search request with nothing on screen to show it.
   */
  enabled?: boolean;
}

function toItem(
  router: ReturnType<typeof useRouter>,
  config: CollectionUiConfig,
  sectionKey: string,
  document: SearchDocument,
  textMatch: number,
  highlight: SearchHighlight
): SearchResultItem {
  return {
    id: readString(document, 'id') ?? `${config.key}-${readString(document, 'sourceId') ?? ''}`,
    collectionKey: config.key,
    sectionKey,
    config,
    accentKey: config.accentKey(document),
    identifier: config.identifier(document),
    typeLabel: config.typeLabel(document),
    status: config.status?.(document),
    slots: selectSlots(config, document, highlight),
    route: buildAppHref(router, config.destination(document)),
    textMatch,
    document,
    highlight,
  };
}

const rankingFor = (config: CollectionUiConfig): MultiSearchLeg['typesense'] => ({
  ...typesenseRanking(config),
  highlightAffixNumTokens: HIGHLIGHT_AFFIX_NUM_TOKENS,
});

/** True when the scoped section's is:open/is:closed filters yield a non-empty set —
 *  the signal (alongside a legal status) that the buckets are narrowed and a
 *  facet-only companion leg is needed to keep the full list switchable. */
function buildLifecycleForFacetLeg(section: SearchSection, filters: readonly Filter[]): boolean {
  if (!section.statusOpenState) return false;
  const wantOpen = filters.some((f) => f.dim === 'lifecycle' && f.open);
  const wantClosed = filters.some((f) => f.dim === 'lifecycle' && !f.open);
  const size =
    (wantOpen ? section.statusOpenState.open.length : 0) + (wantClosed ? section.statusOpenState.closed.length : 0);
  return size > 0;
}

export function useGlobalSearch({
  client,
  collections,
  query,
  scope,
  filters,
  typeFacet = null,
  enabled = true,
}: UseGlobalSearchArgs): GlobalSearchState {
  const router = useRouter();
  const { credentials, refresh } = useSearchCredentials();
  const [groups, setGroups] = useState<SearchResultGroup[]>([]);
  const [statusFacets, setStatusFacets] = useState<StatusFacet[]>([]);
  const [loading, setLoading] = useState(false);
  // See `GlobalSearchState.settled`: the request-completion latch the palette's
  // skeleton reads instead of `groups.length`.
  const [settled, setSettled] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Primitive keys so a fresh-but-equal `collections`/`filters` array does not
  // restart the debounce; the memo (and the effect below) re-run only when the
  // entitled set, the scope, or the filter set actually changes.
  const collectionsKey = collections.join(',');
  const filtersKey = filters.map(filterId).join('|');

  // Stable list of sections to query: scope selects one; else the `type` filters
  // select the breadth (OR across legs). Only sections both the key permits AND
  // the palette can render.
  const searchableSections = useMemo(() => {
    const entitled = entitledRenderableSections(collections);
    return resolveQuerySections(entitled, scope, filters, typeFacet);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the joined
    // primitives so a fresh-but-equal identity does not restart the debounce
  }, [collectionsKey, scope, filtersKey, typeFacet]);

  const scoped = scope !== null;
  const trimmedQuery = query.trim();

  // Guards against an out-of-order response overwriting a newer one. Bumped on
  // every effect run — including the bail-out — so an in-flight response for a
  // now-cleared query can never pass the staleness check below.
  const requestIdRef = useRef(0);

  // The status buckets last seen for the scoped section, so a follow-up
  // status filter can be validated without re-deriving the legal set.
  const statusFacetsRef = useRef<StatusFacet[]>([]);

  // The search effect reads the client at request time and keys on its engine, so a refreshed
  // key (a new client) never restarts the request that is retrying with it.
  const latest = useRef({ client, credentials, refresh });
  useEffect(() => {
    latest.current = { client, credentials, refresh };
  });
  const clientEngine = client?.engine ?? null;

  // The resolved section/scope/filter shape of the last effect run. A change here is a DISCRETE event (a completed token, a chip, entering/leaving scope), so it fires immediately while plain text edits keep the debounce. `null` until the first run so the cold-start search still debounces.
  const queryShapeRef = useRef<string | null>(null);

  useEffect(() => {
    // Fire immediately when the resolved section/filter set changed since the last
    // run (a discrete event); keep the debounce for character typing (same shape).
    const queryShape = `${scope ?? ''}::${typeFacet ?? ''}::${filtersKey}::${searchableSections.map((section) => section.key).join(',')}`;
    const shapeChanged = queryShapeRef.current !== null && queryShapeRef.current !== queryShape;
    queryShapeRef.current = queryShape;

    const searchClient = latest.current.client;
    if (!enabled || !searchClient || trimmedQuery.length === 0 || searchableSections.length === 0) {
      requestIdRef.current += 1;
      setGroups([]);
      setStatusFacets([]);
      statusFacetsRef.current = [];
      setLoading(false);
      // Re-arm the cold-start latch: the next non-empty query is a fresh stream, so
      // its first request should show the skeleton again (not inherit the last
      // stream's settled state).
      setSettled(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    // The scoped section (all-view has no single scoped section). Its live status
    // buckets gate the scoped `status` sub-facet: only a value the section actually
    // reports may reach the filter (the injection guard lives in buildLegFilter).
    const scopedSection = scoped ? searchableSections[0] : undefined;
    const scopedConfig = scopedSection ? getCollectionConfig(scopedSection.collection) : undefined;
    const liveStatusValues = statusFacetsRef.current.map((facet) => facet.value);
    // A legal scoped status value (in the live buckets), for the facet-leg decision.
    const scopedStatusFilter = filters.find((filter) => filter.dim === 'status');
    const legalStatus =
      scoped && scopedStatusFilter && scopedConfig?.hasStatus && liveStatusValues.includes(scopedStatusFilter.value)
        ? scopedStatusFilter.value
        : null;
    // The SCOPED section's is:open/is:closed union — drives the facet-leg decision
    // (a facet-only leg exists solely in the scoped tab).
    const scopedLifecycleClause = scoped && scopedSection ? buildLifecycleForFacetLeg(scopedSection, filters) : false;

    const { engine } = searchClient;
    const controller = new AbortController();
    const timer = setTimeout(
      () => {
        const legs: MultiSearchLeg[] = searchableSections.map((section) => {
          const config = getCollectionConfig(section.collection);
          if (!config) throw new Error(`No render config for section collection ${section.collection}`);
          return {
            collection: config.key,
            query: trimmedQuery,
            filter: buildLegFilter({ section, filters, scoped, liveStatusValues, hasStatus: config.hasStatus, engine }),
            hitsPerPage: scoped ? SCOPED_RESULTS_PER_PAGE : RESULTS_PER_COLLECTION,
            // Scoped tab offers status sub-facets; a chosen status / is:open narrows it.
            ...(scoped && config.hasStatus ? { facets: ['status'] } : {}),
            typesense: rankingFor(config),
          };
        });

        // A status filter collapses the status buckets to the filtered set, so a status narrowing adds a facet-only leg — the section's standing clauses with the results leg's ranking — to keep the full bucket list switchable.
        const statusNarrowed = scoped && (legalStatus !== null || scopedLifecycleClause);
        const facetLegIndex = statusNarrowed ? legs.length : -1;
        if (facetLegIndex !== -1 && scopedSection && scopedConfig) {
          legs.push({
            collection: scopedConfig.key,
            query: trimmedQuery,
            filter: sectionBaseFilter(scopedSection, engine),
            hitsPerPage: 0,
            facets: ['status'],
            typesense: rankingFor(scopedConfig),
          });
        }

        const isCurrent = () => requestIdRef.current === requestId && !controller.signal.aborted;
        // A same-engine key refresh swaps the client without restarting this effect, so the request
        // goes out on the newest one. A rejected key re-mints once and sends the same legs again; a
        // rejection after that lands in the failure path below.
        const { client: requestClient, credentials: requestCredentials, refresh: remint } = latest.current;
        const sender = requestClient ?? searchClient;
        retryOnRefusedKey({
          credentials: requestCredentials,
          send: () => sender.multiSearch(legs, controller.signal),
          remint,
          resend: (fresh) => {
            const retryClient = createSearchClient(fresh);
            return retryClient.engine === sender.engine ? retryClient.multiSearch(legs, controller.signal) : null;
          },
          isCurrent,
        })
          .then((results) => {
            if (requestIdRef.current !== requestId) return;
            const nextGroups = searchableSections
              .map((section, index): SearchResultGroup | null => {
                const config = getCollectionConfig(section.collection);
                if (!config) return null;
                const result = results[index];
                if (result?.error) {
                  // A per-leg error parses cleanly into an empty group; surface it
                  // rather than let it read as a genuine zero-match.
                  console.warn(
                    { collection: config.key, section: section.key, error: result.error },
                    'Search leg failed'
                  );
                  return null;
                }
                const items = (result?.hits ?? []).map((raw) => {
                  const hit = engine === 'algolia' ? fromAlgoliaHit(config.key, raw) : raw;
                  return toItem(router, config, section.key, hit.document, hit.score, hit.highlight);
                });
                return { key: section.key, section, found: result?.found ?? 0, items };
              })
              .filter((group): group is SearchResultGroup => group !== null && group.items.length > 0);

            // In the scoped view the status buckets come from the facet-only leg when
            // a filter is applied (index === legs.length before the push), else
            // from the single scoped result.
            const facetLeg = facetLegIndex === -1 ? 0 : facetLegIndex;
            const facetResult = scoped ? results[facetLeg] : undefined;
            const statusCounts = facetResult?.facetCounts.status ?? [];
            const nextStatusFacets = statusCounts.map((bucket) => ({ value: bucket.value, count: bucket.count }));

            statusFacetsRef.current = nextStatusFacets;
            setGroups(nextGroups);
            setStatusFacets(nextStatusFacets);
            setError(null);
            setLoading(false);
            // First response for this stream has landed — the skeleton yields to the
            // real results / zero state, and later keystrokes keep the settled view.
            setSettled(true);
          })
          .catch((caught: unknown) => {
            if (requestIdRef.current !== requestId) return;
            // An aborted (superseded/cleared) request is not a search failure.
            if (controller.signal.aborted || (caught instanceof Error && caught.name === 'AbortError')) return;
            // A genuine failure is non-fatal: show nothing rather than an error wall.
            setGroups([]);
            setStatusFacets([]);
            statusFacetsRef.current = [];
            setError(caught instanceof Error ? caught : new Error('Search failed'));
            setLoading(false);
            // A failed request still SETTLES the stream, so the empty state shows instead of a perpetual skeleton.
            setSettled(true);
          });
      },
      shapeChanged ? 0 : SEARCH_DEBOUNCE_MS
    );

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `filters` enters via
    // the stable `filtersKey` primitive so a fresh-but-equal array identity does
    // not restart the debounce (mirrors `collectionsKey`).
  }, [enabled, router, clientEngine, trimmedQuery, searchableSections, scoped, filtersKey]);

  const totalFound = useMemo(() => groups.reduce((sum, group) => sum + group.found, 0), [groups]);

  return { groups, totalFound, statusFacets, loading, settled, error };
}
